import { computed } from "vue";

import { useEmployeeViewState } from "@/views/Employees/composables/useEmployeeViewState";
import { useEmployees } from "@/composables/employees/useEmployees";
import { useDropdownItems } from "@/composables/dropdown/useDropdownItems";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";

export function useTableConfig() {
  const viewState = useEmployeeViewState();
  const employee = useEmployees();
  const dropdownItems = useDropdownItems();
  const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
  const sidebarCountStore = useSidebarCountStore();
  const tableParams = computed(() => {
    return {
      headers: [
        {
          text: "Employee",
          sortable: true,
          value: "first_name",
        },
        {
          text: "Primary",
          sortable: false,
          value: "primary",
          img: "icons/briefcase.svg",
        },
        //-----------------------|commented in ER-3439 for v2|-------------------------
        // {
        //   text: "Acting As",
        //   sortable: false,
        //   value: "actingas",
        //   img: "icons/theater.svg",
        // },
        // {
        //   text: "Provides backup to",
        //   sortable: false,
        //   value: "backedup",
        //   img: "icons/briefcaseoutlined.svg",
        // },
        {
          text: "Prepares for",
          sortable: false,
          value: "prepares",
          img: "icons/preparing.svg",
        },
        //-----------------------|commented in ER-3439 for v2|-------------------------
        {
          text: "Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "chatter",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      columns: [
        {
          field: "",
          component: "EmployeeCard",
          props: (item) => ({
            class: "my-4",
            ...viewState.getEmployeeCardProps(item),
          }),
          events: (item) => ({
            "update:availability": async (availabilityValue) => {
              const payload = {
                employee: item,
                value: availabilityValue,
              };
              await employee.onUpdateAvailability({
                accountId: accountID,
                data: payload,
              });
              await employee.loadAllEmployees();
            },
            goToEmployeeForm: (item) => {
              employee.setCurrentEmployee(item);
            },
          }),
        },
        {
          field: "",
          component: "EmployeeAssigmentTag",
          props: (item) => ({
            class: "my-4 custom-assign",
            ...viewState.getEmployeeAssigmentTagProps.getBasicInfo(item),
            ...viewState.getEmployeeAssigmentTagProps.getPositionInfo(item),
            ...viewState.getEmployeeAssigmentTagProps.getTypeInfo(item),
          }),
          events: (item) => ({
            onPrimaryAssign: () => {
              employee.setCurrentEmployee(item);
              viewState.openAssignmentWizard();
            },
            onClose: () => {
              employee.setCurrentEmployee(item);
              viewState.state.relieveAssignment.currentPosition =
                item.primary_position || null;
              viewState.state.relieveAssignment.show = true;
              viewState.state.relieveAssignment.type = "primary";
            },
            positionDetail: () => {
              employee.setCurrentEmployee(item);
              viewState.openAssignmentPosition(item, accountID);
            },
            onHandleApplyDate(date) {
              employee.setCurrentEmployee(item);
              viewState.onApplyDatePrimaryAssignment(date);
            },
          }),
        },
        //-----------------------|commented in ER-3439 for v2|-------------------------
        {
          field: "",
          component: "ExEmployeePrepareAssignmentCard",
          props: (item) => ({
            class: "my-4",
            ...viewState.getEmployeePrepareAssignmentCardProps.getOccupiedByInfo(
              item
            ),
            ...viewState.getEmployeePrepareAssignmentCardProps.getEmployeeInfo(
              item
            ),
            ...viewState.getEmployeePrepareAssignmentCardProps.getPositionInfo(
              item
            ),
            ...viewState.getEmployeePrepareAssignmentCardProps.getTypeInfo(
              item
            ),
          }),
          events: (item) => ({
            onPreparePosition: () => {
              employee.setCurrentEmployee(item);
              viewState.openPreparePosition();
            },
            positionDetail: () => {
              employee.setCurrentEmployee(item);
              viewState.openPreparePositionDialog(item, accountID);
            },
            onClose: () => {
              employee.setCurrentEmployee(item);
              viewState.state.relieveAssignment.currentPosition =
                item.preparing_for_positions[0] || null;
              viewState.state.relieveAssignment.show = true;
              viewState.state.relieveAssignment.type = "prepare_for";
            },
            onHandleApplyDate(date) {
              employee.setCurrentEmployee(item);
              viewState.onApplyDatePrepareFor(date);
            },
          }),
        },
        // {},
        // {},
        //-----------------------|commented in ER-3439 for v2|-------------------------
        {
          field: "",
          component: "EmployeeBadge",
          props: (item) => ({
            ...viewState.getStateBadgeProps(item),
          }),
        },
        {
          field: "",
          component: "ChatterButton",
          props: () => ({
            class: "my-4",
          }),
          events: (item) => ({
            onClick: () => {
              employee.setCurrentEmployee(item);
              viewState.openEmployeeChatterModal();
            },
          }),
        },
        {
          field: "",
          component: "Dropdown",
          props: (item) => ({
            "data-testid": "employee-operations-menu",
            "translate-x-offset": 12,
            "translate-y-offset": 30,
            "is-open": false,
            "dropdown-items": dropdownItems.getEmployeeDropdownItems(item),
          }),
          events: (item) => ({
            onClick: async (operation) => {
              await viewState.handleEmployeeDropdownOperation(operation, item);
            },
          }),
          slotProps: (item) => ({ item: item }),
        },
      ],
      filters: {
        values: viewState.state.filters.values,
        searchText: viewState.state.filters.searchText,
      },
      hideTopSection: false,
      hideActiveNo: false,
      activeRecords: {
        field: "current_status",
        value: "active",
      },
      defaultOrderingField: "first_name",
      activeRecordsCount: sidebarCountStore.employees,
      hideGridView: true, //------| add this to hide bulk action in ER-4046 |------
      actionLabel: "Add Employee",
      title: "Employees Organization",
      orderingStorageKey: "employees.ordering",
      getRowStyle: (item) => ({
        backgroundColor:
          item.position?.data?.status === "active" ? "#ECFDF3" : "#FFFFFF",
      }),
      cellContainerMaxWidth: "calc(15vw + 60px)",
    };
  });
  return {
    tableParams,
  };
}
